/**
 * An SOP's scenario types and how many scenarios of each kind sit under each, from one call.
 * Read as a query rather than persisted: the counts move whenever past tickets are bucketed or
 * scenarios fabricated, so a copy that outlived the page would report scenarios the SOP no
 * longer has.
 */

import { useMemo } from 'react';
import { useAtom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { queryClient } from '@/lib/query-client';
import { aiFunctionService, type ScenarioCounts } from '@/services/ai-function.service';
import { toLocalType, type ScenarioType } from '@/modules/back-testing/types';

export interface SopTypes {
	types: ScenarioType[];
	/** Scenario counts by type id, plus `none` for the past tickets that fit no type. */
	counts: Record<string, ScenarioCounts>;
}

export const backTestingTypesKey = (sopId: string): string[] => ['back-testing', sopId, 'types'];

const backTestingTypesQueryAtom = (sopId: string) =>
	atomWithQuery(() => ({
		queryKey: backTestingTypesKey(sopId),
		queryFn: async (): Promise<SopTypes> => {
			const data = await aiFunctionService.fetchTypes(sopId);
			return { types: data.types.map((t) => toLocalType(sopId, t)), counts: data.counts };
		},
		enabled: Boolean(sopId),
		// Generating types, deleting and bucketing all write the answer back themselves,
		// so this only covers a screen left open while someone else changes the set.
		staleTime: 2 * 60 * 1000
	}));

export function useBackTestingTypes(sopId: string) {
	const queryAtom = useMemo(() => backTestingTypesQueryAtom(sopId), [sopId]);
	const [query] = useAtom(queryAtom);
	return query;
}

/** Fetches the types and counts again; the caller has just changed them on the server. */
export const reloadBackTestingTypes = (sopId: string): Promise<void> =>
	queryClient.invalidateQueries({ queryKey: backTestingTypesKey(sopId) }).then(() => undefined);

/**
 * A generated set replaces the whole list, and the counts go with it: the new set has
 * fresh type ids and the server drops every scenario under the old ones, so the old
 * counts describe types that no longer exist. Zero is the truth until bucketing reports.
 */
export const setBackTestingTypes = (sopId: string, types: ScenarioType[]): void => {
	queryClient.setQueryData<SopTypes>(backTestingTypesKey(sopId), { types, counts: {} });
};

/** Drops one type and its count. */
export const removeBackTestingType = (sopId: string, typeId: string): void => {
	queryClient.setQueryData<SopTypes>(backTestingTypesKey(sopId), (prev) => {
		if (!prev) return prev;
		const counts = { ...prev.counts };
		delete counts[typeId];
		return { types: prev.types.filter((t) => t.id !== typeId), counts };
	});
};

/** Everything back testing held for one SOP is gone; the next read starts from the server. */
export const clearBackTestingTypes = (sopId: string): void => {
	queryClient.removeQueries({ queryKey: backTestingTypesKey(sopId) });
};
